import React from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import clsx from 'clsx';
import ReactFileReader from 'react-file-reader';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  grid: {
    maxWidth: '100%',
    margin: 'auto',
  },
  gridItem: {
    width: '100%',
    maxWidth: '300px',
  },
  mobileGridItem: {
    width: '100%',
  },
  paper: {
    minWidth: '200px',
    minHeight: '125px',
    maxWidth: '300px',
    padding: theme.spacing(2),
    border: '5px',
    textAlign: 'center',
    display: 'flex',
    height: '100%',
  },
  mobilePaper: {
    padding: theme.spacing(1),
    textAlign: 'center',
    display: 'flex',
    overflow: 'hidden',
  },
  cardContent: {
    color: theme.palette.text.primary,
    fontSize: '1.75em',
    padding: '1vh',
    fontWeight: 'bold',
    verticalAlign: 'middle',
    width: '100%',
    margin: 'auto',
  },
  mobileCardContent: {
    color: theme.palette.text.primary,
    fontSize: '1.75em',
    fontWeight: 'bold',
    verticalAlign: 'middle',
    width: '100%',
    margin: 'auto',
  },
  linkTile: {
    cursor: 'pointer',
    display: 'block',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  blue: {
    backgroundColor: '#1976D2',
  },
  green: {
    backgroundColor: '#388E3C',
  },
  red: {
    backgroundColor: '#d32f2f',
  },
  purple: {
    backgroundColor: '#512DA8',
  },
  grey: {
    backgroundColor: '#455A64',
  },
  pink: {
    backgroundColor: '#ff0080',
  },
  orange: {
    backgroundColor: '#E64A19',
  },
  teal: {
    backgroundColor: '#0097A7',
  },
  brown: {
    backgroundColor: '#5D4037',
  },
  hover: {
    '&:hover': {
      filter: 'brightness(115%)',
      transform: 'scale(1.05)',
      borderRadius: '3px',
      boxShadow: '-13px 11px 25px 1px rgba(0,0,0,0.55)',
      transition: '.3s',
    },
  },
}));

export interface DashboardButton {
  onClick?: any;
  visible?: boolean;
  color?: string;
  title?: string;
  fileReaderInputProps?: any;
  fileReader?: boolean;
  content?: any;
}

export default function DashboardComponent({ buttons = [], toast, title = '' }: { buttons: DashboardButton[]; toast?: any; title?: string }) {
  toast.dismiss();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  const createTitle = (title) => {
    return <h1>{title}</h1>;
  };

  const createContent = (button: DashboardButton, key: string | number | undefined, isMobile: boolean) => {
    const fileReader = (content: any) => {
      return (
        <ReactFileReader key={key} {...button.fileReaderInputProps}>
          {content}
        </ReactFileReader>
      );
    };

    const defaultContent = (
      <span className={classes.linkTile} onClick={button.onClick}>
        <Paper className={clsx(isMobile ? classes.mobilePaper : classes.paper, classes.hover, classes[button.color ?? theme.palette.primary.main])}>
          <div className={isMobile ? classes.mobileCardContent : classes.cardContent}>{button.title}</div>
        </Paper>
      </span>
    );

    return button.fileReader ? fileReader(button.content ?? defaultContent) : defaultContent;
  };

  const createButton = (button: DashboardButton, key: string | number | undefined, isMobile: boolean) => {
    const item = (
      <Grid item zeroMinWidth className={isMobile ? classes.mobileGridItem : classes.gridItem} key={key}>
        {createContent(button, key, isMobile)}
      </Grid>
    );
    return button.visible ? item : null;
  };

  return (
    <div>
      {title ? createTitle(title) : null}
      <div className={classes.root}>
        <Grid container direction='row' spacing={isMobile ? 1 : 3} justify='center' className={classes.grid}>
          {buttons.map((button, i) => createButton(button, i, isMobile))}
        </Grid>
      </div>
    </div>
  );
}
